import {
  AutocompleteWithLabel,
  CountrySelectorForHeader,
  InputWithLabel,
  RatingWithLabel,
  SelectWithLabel,
  TextAreaWithLabel
} from '@adotel/components'
import { useQuery } from '@apollo/client'
import { MenuItem } from '@material-ui/core'
import { FormikErrors, FormikTouched } from 'formik'
import React, { useEffect, useState } from 'react'

import PageHeader from '../../components/PageHeader'
import SectionTitle from '../../components/SectionTitle'
import { TENANTS_QUERY } from '../../graphql/tenant'
import { CountryType, countries } from '../../helpers/countriesList'
import { numbersOnlyRegex } from '../../helpers/regex'
import { timezones } from '../../helpers/timezones'
import { isFaAvailableInLanguages } from '../../helpers/userData'
import { getTranslate, lang } from '../../localization'
import { Accommodation, ResourceType } from '../../types/accommodation'
import { Tenant } from '../../types/tenants'

interface AccommodationBasicDetailsProps {
  values: Accommodation
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => void
  errors: FormikErrors<Accommodation>
  touched: FormikTouched<Accommodation>
  handleBlur: any
  isSubmitting: any
  classes: any
  loading: boolean
  isForModify: boolean
}

export const AccommodationBasicDetails: React.FC<
  AccommodationBasicDetailsProps
> = ({
  values,
  setFieldValue,
  errors,
  touched,
  handleBlur,
  isSubmitting,
  classes,
  loading,
  isForModify
}) => {
  const [selectedCountryForName, setselectedCountryForName] =
    useState<CountryType>(countries[0])

  const [selectedCountryForDescription, setselectedCountryForDescription] =
    useState<CountryType>(countries[0])

  const nameLanguage =
    selectedCountryForName?.nativeLanguage && lang !== 'en'
      ? selectedCountryForName.nativeLanguage
      : selectedCountryForName?.language

  const descriptionLanguage =
    selectedCountryForDescription?.nativeLanguage && lang !== 'en'
      ? selectedCountryForDescription.nativeLanguage
      : selectedCountryForDescription?.language
  const { data, loading: loadingForTenantsList } = useQuery<{
    tenants: Tenant[]
  }>(TENANTS_QUERY)

  const [autoCompleteLabelForTimezone, setautoCompleteLabelForTimezone] =
    useState<any>(null)
  const [autoCompleteLabelForTenant, setautoCompleteLabelForTenant] =
    useState<any>(null)

  useEffect(() => {
    if (loadingForTenantsList || !values?.basicDetails?.tenantId) {
      return
    }
    const tenant = data?.tenants.find(
      (t) =>
        t.basicDetails.displayTenantCode ===
        values?.basicDetails?.displayTenantCode
    )

    setautoCompleteLabelForTenant(
      tenant
        ? {
            value: values?.basicDetails?.tenantId,
            label: tenant.basicDetails.name
          }
        : null
    )

    setFieldValue(
      'basicDetails.tenantCode',
      tenant ? tenant.basicDetails.tenantCode : ''
    )
  }, [values?.basicDetails?.tenantId, loadingForTenantsList])

  useEffect(() => {
    const timezone = timezones.find((tz) =>
      tz.utc.includes(values?.basicDetails?.timeZone)
    )

    setautoCompleteLabelForTimezone(
      timezone
        ? {
            value: values?.basicDetails?.timeZone,
            label: timezone.text || ''
          }
        : null
    )
  }, [values?.basicDetails?.timeZone])

  const resourceTypeOptions: ResourceType[] = ['Hotel', 'Motel', 'BNB']

  return (
    <>
      <PageHeader
        title={
          !isForModify
            ? 'Accommodation'
            : lang === 'en'
            ? values?.basicDetails?.name
            : values?.basicDetails?.nameTranslatedList.find(
                (item) => item.label === selectedCountryForName.label
              )?.value
        }
      />
      <div className={classes.titleContainer}>
        <SectionTitle title="Basic Details" marginBottom />
      </div>
      <div className={classes.fieldsContainer}>
        <InputWithLabel
          data-cy="basicDetailsName"
          name="basicDetails.name"
          label={getTranslate('Name')}
          placeholder={getTranslate('Enter the accommodation name')}
          autoComplete="off"
          value={values?.basicDetails?.name}
          onChange={(event: { currentTarget: { value: any } }) =>
            setFieldValue('basicDetails.name', event.currentTarget.value)
          }
          error={touched.basicDetails?.name && !!errors.basicDetails?.name}
          helpertext={
            touched.basicDetails?.name
              ? getTranslate(errors.basicDetails?.name || '')
              : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
          inputSize="small"
        />
        {isFaAvailableInLanguages() && (
          <InputWithLabel
            data-cy="basicDetailsPersianName"
            label={`${lang === 'en' ? nameLanguage : ''} ${getTranslate(
              'Name'
            )} ${lang === 'fa' ? nameLanguage : ''}`}
            placeholder={`${getTranslate(
              'Enter the accommodation name in'
            )} ${nameLanguage}`}
            autoComplete="off"
            id={`basicDetails.nameTranslatedList[${values?.basicDetails?.nameTranslatedList.findIndex(
              (item) => item.label === selectedCountryForName.label
            )}].value`}
            name={`basicDetails.nameTranslatedList[${values?.basicDetails?.nameTranslatedList.findIndex(
              (item) => item.label === selectedCountryForName.label
            )}].value`}
            value={
              values?.basicDetails?.nameTranslatedList.find(
                (item) => item.label === selectedCountryForName.label
              )?.value
            }
            onChange={(event: {
              currentTarget: { value: React.SetStateAction<string> }
            }) => {
              setFieldValue(
                `basicDetails.nameTranslatedList[${values?.basicDetails?.nameTranslatedList.findIndex(
                  (item) => item.label === selectedCountryForName.label
                )}].value`,
                event.currentTarget.value
              )
            }}
            error={
              (touched.basicDetails?.nameTranslatedList &&
                !values?.basicDetails?.nameTranslatedList[0].value) ||
              undefined
            }
            helpertext={
              touched.basicDetails?.nameTranslatedList &&
              !values?.basicDetails?.nameTranslatedList[0].value
                ? getTranslate('This field is required')
                : ''
            }
            onBlur={handleBlur}
            disabled={isSubmitting}
            inputSize="small"
            rightHeader={
              <CountrySelectorForHeader
                selectedCountry={selectedCountryForName}
                onChange={(item: CountryType) => {
                  const translatedName = {
                    label: item.label,
                    nativeLabel: item.nativeLanguage,
                    value: ''
                  }
                  const tempTranslatedArr = [
                    ...values?.basicDetails?.nameTranslatedList,
                    values?.basicDetails?.nameTranslatedList.find(
                      (singleName) => singleName.label === item.label
                    )
                      ? undefined
                      : translatedName
                  ]
                  setFieldValue(
                    'basicDetails.nameTranslatedList',
                    tempTranslatedArr
                  )
                  setselectedCountryForName(item)
                }}
              />
            }
          />
        )}
        <InputWithLabel
          data-cy="basicDetailsPropertyCode"
          name="basicDetails.propertyCode"
          label={getTranslate('Property Code')}
          placeholder={getTranslate('Choose a property code for accommodation')}
          autoComplete="off"
          value={
            isForModify
              ? values?.basicDetails?.displayPropertyCode
              : values?.basicDetails?.propertyCode
          }
          onChange={(event: { currentTarget: { value: string } }) => {
            const inputValue = event.currentTarget.value

            if (numbersOnlyRegex.test(inputValue) || inputValue === '') {
              setFieldValue('basicDetails.propertyCode', inputValue)
            }
          }}
          error={
            (touched.basicDetails?.propertyCode &&
              !!errors.basicDetails?.propertyCode) ||
            undefined
          }
          helpertext={
            touched.basicDetails?.propertyCode
              ? getTranslate(errors.basicDetails?.propertyCode || '')
              : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting || isForModify}
          inputSize="small"
        />
        <div>
          <SelectWithLabel
            data-cy="basicDetailsResourceType"
            name="basicDetails.resourceType"
            fullWidth
            value={values?.basicDetails?.resourceType}
            onChange={(event: { target: { value: any } }) =>
              setFieldValue(
                'basicDetails.resourceType',
                event.target.value as ResourceType
              )
            }
            size="small"
            label={getTranslate('Resource Type')}
            error={
              (touched.basicDetails?.resourceType &&
                !!errors.basicDetails?.resourceType) ||
              undefined
            }
            helpertext={
              touched.basicDetails?.resourceType
                ? getTranslate(errors.basicDetails?.resourceType || '')
                : undefined
            }>
            {resourceTypeOptions.map((rt) => (
              <MenuItem
                key={rt}
                value={rt}
                data-cy={rt === 'Hotel' ? 'MenuItemHotel' : undefined}>
                {rt}
              </MenuItem>
            ))}
          </SelectWithLabel>
          {/* {selectedResourceType && (
                <IconButton
                  className={classes.clearIconButton}
                  onClick={() => setselectedResourceType('')}>
                  <ClearIcon className={classes.clearIcon} />
                </IconButton>
              )} */}
        </div>
        <InputWithLabel
          data-cy="basicDetailsWebsite"
          name="basicDetails.website"
          label={getTranslate('Website')}
          placeholder={getTranslate('Enter the accommodation website')}
          autoComplete="off"
          value={values?.basicDetails?.website}
          onChange={(event: { currentTarget: { value: any } }) =>
            setFieldValue('basicDetails.website', event.currentTarget.value)
          }
          error={
            (touched.basicDetails?.website && !!errors.basicDetails?.website) ||
            undefined
          }
          helpertext={
            touched.basicDetails?.website
              ? getTranslate(errors.basicDetails?.website || '')
              : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
          inputSize="small"
        />
        <AutocompleteWithLabel
          data-cy="basicDetailsTimeZone"
          name="basicDetails.timeZone"
          label={getTranslate('Timezone')}
          placeholder={getTranslate('Select accommodation timezone')}
          onChange={(event, v: any) => {
            if (v) {
              setautoCompleteLabelForTimezone({
                value: v.value,
                label: v.label
              })
              setFieldValue('basicDetails.timeZone', v.value)
            } else {
              setautoCompleteLabelForTimezone(null)
              setFieldValue('basicDetails.timeZone', '')
            }
          }}
          getOptionLabel={(option) => option?.label || ''}
          options={timezones.map((item) => ({
            label: item.text,
            value: item.utc[0]
          }))}
          renderOption={(option) => <>{option.label}</>}
          getOptionSelected={(option, value) => option.value === value.value}
          size="small"
          value={autoCompleteLabelForTimezone}
          includeInputInList
          error={
            (touched.basicDetails?.timeZone &&
              !!errors.basicDetails?.timeZone) ||
            undefined
          }
          helpertext={
            touched.basicDetails?.timeZone
              ? getTranslate(errors.basicDetails?.timeZone || '')
              : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
          inputSize="small"
          loading={loading}
          loadingText={getTranslate('Loading') + '...'}
        />
        <AutocompleteWithLabel
          name="basicDetails.tenantId"
          label={getTranslate('Tenant')}
          placeholder={getTranslate('Select accommodation tenant')}
          includeInputInList
          onChange={(_, v: any) => {
            if (v) {
              setautoCompleteLabelForTenant({
                value: v.value || '',
                label: v.label || '',
                code: v.code || ''
              })
              setFieldValue('basicDetails.tenantCode', v.code)
              setFieldValue('basicDetails.displayTenantCode', v.id)
              setFieldValue('basicDetails.tenantId', v.value)
            } else {
              setautoCompleteLabelForTenant(null)
              setFieldValue('basicDetails.tenantCode', 0)
              setFieldValue('basicDetails.displayTenantCode', 0)
              setFieldValue('basicDetails.tenantId', '')
            }
          }}
          getOptionSelected={(option, value) => option.value === value.value}
          getOptionLabel={(option) => option?.label || ''}
          options={
            data?.tenants?.map((item) => ({
              label: item.basicDetails.name,
              value: item.basicDetails?.tenantId || '',
              code: item.basicDetails?.tenantCode || '',
              id: item.basicDetails.displayTenantCode
            })) || []
          }
          renderOption={(option) => <>{option.label}</>}
          size="small"
          value={autoCompleteLabelForTenant}
          error={
            (touched.basicDetails?.tenantId &&
              !!errors.basicDetails?.tenantId) ||
            undefined
          }
          helpertext={
            touched.basicDetails?.tenantId
              ? getTranslate(errors.basicDetails?.tenantId || '')
              : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting || isForModify}
          inputSize="small"
          loading={loading}
          loadingText={getTranslate('Loading' + '...')}
          data-cy="basicDetailsTenant"
        />
        <InputWithLabel
          data-cy="basicDetailsTotaRoomNumber"
          name="basicDetails.totaRoomNumber"
          label={getTranslate('Number Of Rooms')}
          placeholder={getTranslate('Enter the accommodation number of rooms')}
          autoComplete="off"
          value={values?.basicDetails?.totaRoomNumber}
          onChange={(event: { currentTarget: { value: any } }) =>
            setFieldValue(
              'basicDetails.totaRoomNumber',
              typeof parseInt(event.currentTarget.value, 10) === 'number' &&
                !isNaN(parseInt(event.currentTarget.value, 10))
                ? parseInt(event.currentTarget.value, 10)
                : parseInt('0')
            )
          }
          error={
            (touched.basicDetails?.totaRoomNumber &&
              !!errors.basicDetails?.totaRoomNumber) ||
            undefined
          }
          helpertext={
            touched.basicDetails?.totaRoomNumber
              ? getTranslate(errors.basicDetails?.totaRoomNumber || '')
              : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
          inputSize="small"
        />
        <InputWithLabel
          placeholder={getTranslate('00:00')}
          inputSize="small"
          name="basicDetails.checkinFrom"
          label={getTranslate('Checkin From')}
          value={values.basicDetails.checkinFrom}
          onChange={(event) =>
            setFieldValue('basicDetails.checkinFrom', event.currentTarget.value)
          }
          error={
            (touched.basicDetails?.checkinFrom &&
              !!errors.basicDetails?.checkinFrom) ||
            undefined
          }
          helpertext={
            touched.basicDetails?.checkinFrom
              ? getTranslate(errors.basicDetails?.checkinFrom || '')
              : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
        />
        <InputWithLabel
          placeholder={getTranslate('00:00')}
          inputSize="small"
          name="basicDetails.checkinTo"
          label={getTranslate('Checkin To')}
          value={values.basicDetails.checkinTo}
          onChange={(event) =>
            setFieldValue('basicDetails.checkinTo', event.currentTarget.value)
          }
          error={
            (touched.basicDetails?.checkinTo &&
              !!errors.basicDetails?.checkinTo) ||
            undefined
          }
          helpertext={
            touched.basicDetails?.checkinTo
              ? getTranslate(errors.basicDetails?.checkinTo || '')
              : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
        />
        <InputWithLabel
          placeholder={getTranslate('00:00')}
          inputSize="small"
          name="basicDetails.checkoutFrom"
          label={getTranslate('Checkout From')}
          value={values.basicDetails.checkoutFrom}
          onChange={(event) =>
            setFieldValue(
              'basicDetails.checkoutFrom',
              event.currentTarget.value
            )
          }
          error={
            (touched.basicDetails?.checkoutFrom &&
              !!errors.basicDetails?.checkoutFrom) ||
            undefined
          }
          helpertext={
            touched.basicDetails?.checkoutFrom
              ? getTranslate(errors.basicDetails?.checkoutFrom || '')
              : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
        />
        <InputWithLabel
          placeholder={getTranslate('00:00')}
          inputSize="small"
          name="basicDetails.checkoutTo"
          label={getTranslate('Checkout To')}
          value={values.basicDetails.checkoutTo}
          onChange={(event) =>
            setFieldValue('basicDetails.checkoutTo', event.currentTarget.value)
          }
          error={
            (touched.basicDetails?.checkoutTo &&
              !!errors.basicDetails?.checkoutTo) ||
            undefined
          }
          helpertext={
            touched.basicDetails?.checkoutTo
              ? getTranslate(errors.basicDetails?.checkoutTo || '')
              : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
        />
        {!isFaAvailableInLanguages() && <div />}
        <TextAreaWithLabel
          data-cy="basicDetailsDescription"
          name="basicDetails.description"
          label={getTranslate('Description')}
          placeholder={getTranslate('Enter the accommodation description')}
          autoComplete="off"
          value={values?.basicDetails?.description}
          onChange={(event: { currentTarget: { value: any } }) =>
            setFieldValue('basicDetails.description', event.currentTarget.value)
          }
          error={
            (touched.basicDetails?.description &&
              !!errors.basicDetails?.description) ||
            undefined
          }
          helpertext={
            touched.basicDetails?.description
              ? getTranslate(errors.basicDetails?.description || '')
              : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
          inputSize="small"
          rows={7}
        />
        {isFaAvailableInLanguages() && (
          <TextAreaWithLabel
            data-cy="basicDetailsPersianDescription"
            label={`${lang === 'en' ? descriptionLanguage : ''} ${getTranslate(
              'Description'
            )} ${lang === 'fa' ? descriptionLanguage : ''}`}
            placeholder={`${getTranslate(
              'Enter the accommodation description in'
            )} ${descriptionLanguage}`}
            autoComplete="off"
            id={`basicDetails.descriptionTranslatedList[${values?.basicDetails?.descriptionTranslatedList.findIndex(
              (item) => item.label === selectedCountryForDescription.label
            )}].value`}
            name={`basicDetails.descriptionTranslatedList[${values?.basicDetails?.descriptionTranslatedList.findIndex(
              (item) => item.label === selectedCountryForDescription.label
            )}].value`}
            value={
              values?.basicDetails?.descriptionTranslatedList.find(
                (item) => item.label === selectedCountryForDescription.label
              )?.value
            }
            onChange={(event: {
              currentTarget: { value: React.SetStateAction<string> }
            }) => {
              setFieldValue(
                `basicDetails.descriptionTranslatedList[${values?.basicDetails?.descriptionTranslatedList.findIndex(
                  (item) => item.label === selectedCountryForDescription.label
                )}].value`,
                event.currentTarget.value
              )
            }}
            error={
              (touched.basicDetails?.descriptionTranslatedList &&
                !values?.basicDetails?.descriptionTranslatedList[0].value) ||
              undefined
            }
            helpertext={
              touched.basicDetails?.descriptionTranslatedList &&
              !values?.basicDetails?.descriptionTranslatedList[0].value
                ? getTranslate('This field is required')
                : ''
            }
            onBlur={handleBlur}
            disabled={isSubmitting}
            inputSize="small"
            rows={7}
            rightHeader={
              <CountrySelectorForHeader
                selectedCountry={selectedCountryForDescription}
                onChange={(item: CountryType) => {
                  const translatedDescription = {
                    label: item.label,
                    nativeLabel: item.nativeLanguage,
                    value: ''
                  }
                  const tempTranslatedArr = [
                    ...values?.basicDetails?.descriptionTranslatedList,
                    values?.basicDetails?.descriptionTranslatedList.find(
                      (singleDescription) =>
                        singleDescription.label === item.label
                    )
                      ? undefined
                      : translatedDescription
                  ]
                  setFieldValue(
                    'basicDetails.descriptionTranslatedList',
                    tempTranslatedArr
                  )
                  setselectedCountryForDescription(item)
                }}
              />
            }
          />
        )}
        <RatingWithLabel
          data-cy="basicDetailsStarClass"
          name="basicDetails.starClass"
          value={values?.basicDetails?.starClass}
          onChange={(event: any, value: any) =>
            setFieldValue('basicDetails.starClass', value)
          }
          defaultValue={3}
          max={5}
          label={getTranslate('Star Class')}
        />
      </div>
    </>
  )
}
